import { ChevronRight } from "lucide-react";
import PropTypes from "prop-types";
import Datatable from "@components/datatable";
import Select from "@components/ui-components/select";
import StatusChip from "@components/ui-components/status-chip";
import { formatDate } from "@helpers/date";
import DebitNotePreviewModal from "@panel/shared/debit-note/components/debit-note-preview-modal";
import useDebitNotePreview from "@panel/shared/debit-note/hooks/use-debit-note-preview";
import RevertDebitNoteModal from "../shared/revert-debit-note-modal";
import DebitNoteActions from "./components/debit-note-actions";
import useDebitNotes from "./hooks/use-debit-notes";
import { STATUS_CAPTION } from "./misc/constants";
import { DEBIT_NOTE_STATUS_LABEL, DEBIT_NOTE_STATUS_OPTIONS, DEBIT_NOTE_STATUS_TONE } from "@src/utility/constants/panel/common";
import { formatCurrency } from "@src/utility/helpers/utils";

const DebitNotes = ({ brandId, brand }) => {
    const {
        notes, pagination, loading,
        filters, handleFilterChange,
        revertTarget, openRevert, closeRevert,
        revertNote, markAsReceived, issueToBrand, fetchInvoices,
        handlePageChange, handlePerRowsChange
    } = useDebitNotes(brandId);
    const preview = useDebitNotePreview({ notes, fetchInvoices });

    const columns = [
        { Header: "Debit note", accessor: "debit_note_number", Cell: ({ value }) => <span className="font-mono text-sm font-medium text-gray-900">{value}</span> },
        { Header: "Period", accessor: "payout_period", Cell: ({ value }) => <span className="text-gray-700">{value ? formatDate(value, "MMMM YYYY") : "—"}</span> },
        { Header: "Created", accessor: "createdAt", Cell: ({ value }) => <span className="text-gray-700">{formatDate(value)}</span> },
        {
            Header  : "Status",
            accessor: "status",
            Cell    : ({ value }) => <StatusChip tone={DEBIT_NOTE_STATUS_TONE[value] || "neutral"} label={DEBIT_NOTE_STATUS_LABEL[value] || value} />
        },
        {
            Header  : "Amount",
            accessor: "amount",
            Cell    : ({ value, row }) => <span className="font-medium text-gray-900">{formatCurrency(value, row.currency)}</span>
        },
        {
            Header  : "",
            accessor: "_id",
            Cell    : () => (
                <span className="flex items-center justify-end gap-1 text-sm font-medium text-accent">
                    View
                    <ChevronRight className="h-3.5 w-3.5" />
                </span>
            )
        }
    ];

    return (
        <div>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-muted">{pagination.total} debit note(s) for {brand.name}.</p>
                <Select
                    id="debit-note-status-filter"
                    placeholder="Any status"
                    value={filters.status}
                    options={DEBIT_NOTE_STATUS_OPTIONS}
                    onChange={(status) => handleFilterChange({ status })} />
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <Datatable
                    columns={columns}
                    data={notes}
                    loading={loading}
                    pagination={{ count: pagination.total, page: pagination.page, limit: pagination.limit }}
                    handlePageChange={handlePageChange}
                    handlePerRowsChange={handlePerRowsChange}
                    onRowClicked={preview.openPreview}
                    customNoData={(
                        <span className="text-sm text-muted">
                            {filters.status ? "No debit notes match this filter." : "No active debit notes yet."}
                        </span>
                    )} />
            </div>

            <DebitNotePreviewModal
                preview={preview}
                brand={brand}
                caption={STATUS_CAPTION[preview.previewNote?.status]}
                actions={preview.previewNote && (
                    <DebitNoteActions
                        note={preview.previewNote}
                        onIssue={issueToBrand}
                        onRevert={openRevert}
                        onMarkAsReceived={markAsReceived} />
                )} />

            <RevertDebitNoteModal
                open={Boolean(revertTarget)}
                onClose={closeRevert}
                debitNoteLabel={revertTarget?.debit_note_number || ""}
                onRevert={(reason) => revertNote(revertTarget._id, reason)} />
        </div>
    );
};

DebitNotes.propTypes = {
    brandId: PropTypes.string.isRequired,
    brand  : PropTypes.object.isRequired
};

export default DebitNotes;
